import type { GmailSendAsDto, IntegrationDto } from '@nsb/core';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, errorText, queryClient, useApi } from '../../api.ts';
import { useFeedbackRecord } from '../../components/feedback-context.ts';
import { Button, ErrorLine, Field, LoadError, Loading, Page, TextInput, Title } from '../../components/ui.tsx';
import { useUnsaved } from '../../components/useUnsaved.ts';
import { isAdmin, useMe } from '../../session.tsx';
import { sameValue } from '../../unsaved.ts';

const NAMES: Record<IntegrationDto['provider'], string> = { gmail: 'Gmail', resend: 'Resend', xero: 'Xero', signing: 'Signing', openai: 'OpenAI', claude: 'Claude', google_places: 'Google Places' };

/** Literal status words, never colour alone (style guide v2 §12: a utility ledger). */
const STATUS: Record<IntegrationDto['status'], { word: string; tone: string }> = {
  connected: { word: 'Connected', tone: '!text-moss-deep' },
  error: { word: 'Needs attention', tone: '!text-alert' },
  not_connected: { word: 'Not connected', tone: '' },
};

/** Systems connected with a pasted key rather than an OAuth sign-in. */
const KEYED: Partial<Record<IntegrationDto['provider'], number>> = { resend: 20, signing: 8, openai: 20, claude: 20, google_places: 20 };

/** A key's format, where it has one to show (Resend's keys begin re_, Google's AIza). */
const KEY_FORMAT: Partial<Record<IntegrationDto['provider'], string>> = { resend: 're_…', google_places: 'AIza…' };

/**
 * Settings → Connections (spec §11): every third-party account the portal
 * uses, connected here once, with literal status words (style guide v2 §12:
 * a utility ledger). An OAuth sign-in comes back here with its answer
 * (?connected=<provider> or ?error=<message>).
 */
export function ConnectionsSettings() {
  const { data: me } = useMe();
  const admin = isAdmin(me);
  const [params, setParams] = useSearchParams();
  const { data: integrations, error } = useApi<IntegrationDto[]>(['integrations'], '/integrations');
  useFeedbackRecord(null, null);
  const connected = params.get('connected');
  const oauthError = params.get('error');
  if (error) return <LoadError error={error} />;
  return (
    <Page>
      <Title kicker="Settings">Connected accounts</Title>
      <p className="mb-6 max-w-measure text-ink-2">Every outside account the portal works through, connected here once: mail, accounting, signing, address suggestions, and the models that read and paint.</p>
      {connected || oauthError ? (
        <p className={`mb-6 text-sm ${oauthError ? 'text-alert' : 'text-moss-deep'}`}>
          {oauthError ? `Could not connect: ${oauthError}` : `${NAMES[connected as IntegrationDto['provider']] ?? connected} is connected.`}{' '}
          <button type="button" className="link" onClick={() => setParams({})}>
            Dismiss
          </button>
        </p>
      ) : null}
      {!integrations ? (
        <Loading />
      ) : (
        <ul className="divide-y divide-rule border-y border-rule">
          {integrations.map((i) => (
            <IntegrationRow key={i.provider} i={i} admin={admin} resendOn={integrations.some((x) => x.provider === 'resend' && x.status !== 'not_connected')} />
          ))}
        </ul>
      )}
    </Page>
  );
}

/** The accounts the nightly check tries (api domain/key-check.ts); Xero and signing have nothing to try until phase 4. */
const TRIED = new Set<IntegrationDto['provider']>(['openai', 'claude', 'gmail', 'resend', 'google_places']);

/**
 * Whether it still works (Jason, 2026-10-03: "a check every day at midnight
 * that the key is still valid and then it asks me to renew"): the last check
 * in words, and Check now. Nobody enters an expiry date.
 */
function CheckLine({ i, admin }: { i: IntegrationDto; admin: boolean }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!TRIED.has(i.provider) || !i.checkWords) return null;
  const check = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/integrations/${i.provider}/check`, { body: {} });
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      await queryClient.invalidateQueries({ queryKey: ['integrations', 'attention'] });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-2">
      <p className={`text-sm ${i.status === 'error' ? 'text-alert' : 'text-ink-2'}`}>
        {i.checkWords}{' '}
        {admin ? (
          <button type="button" className="link min-h-9 whitespace-nowrap" disabled={busy} onClick={() => void check()}>
            {busy ? 'Checking…' : 'Check now'}
          </button>
        ) : null}
      </p>
      <ErrorLine error={err} />
    </div>
  );
}

/**
 * Send me a test: one email to you, the way the portal's own emails go now. On Resend's row once it is connected, on
 * Gmail's only until then (api routes/org.ts).
 */
function TestButton({ provider, run, setNote }: { provider: 'gmail' | 'resend'; run: (fn: () => Promise<void>) => Promise<void>; setNote: (n: string) => void }) {
  return (
    <Button
      tone="quiet"
      onClick={() =>
        void run(async () => {
          const r = await api<{ status: string; error: string | null }>(`/integrations/${provider}/test`, { body: {} });
          setNote(r.status === 'sent' ? 'Test message sent to you.' : `Not sent: ${r.error}`);
        })
      }
    >
      Send me a test
    </Button>
  );
}

function IntegrationRow({ i, admin, resendOn }: { i: IntegrationDto; admin: boolean; resendOn: boolean }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [key, setKey] = useState('');
  const [replacing, setReplacing] = useState(false);
  const [alias, setAlias] = useState(i.intakeAlias ?? '');
  const [note, setNote] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['integrations'] });
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setErr(null);
    setNote(null);
    try {
      await fn();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const connect = () =>
    run(async () => {
      const r = await api<{ authorizeUrl: string }>(`/integrations/${i.provider}/connect`, { body: {} });
      window.location.href = r.authorizeUrl;
    });
  const disconnect = () =>
    run(async () => {
      await api(`/integrations/${i.provider}/disconnect`, { body: {} });
      await refresh();
    });
  const saveKey = () =>
    void run(async () => {
      await api(`/integrations/${i.provider}`, { method: 'PUT', body: { apiKey: key.trim() } });
      setKey('');
      setReplacing(false);
      await refresh();
      await queryClient.invalidateQueries({ queryKey: ['integrations', 'attention'] });
    });
  const saveAlias = () =>
    void run(async () => {
      await api('/integrations/gmail', { method: 'PATCH', body: { intakeAlias: alias.trim() || null } });
      await refresh();
      setNote('Intake alias saved.');
    });
  // A key typed in, and the intake address changed, not saved: each on the bar at the window's foot, which saves as its
  // own button does (P-29). Only by its label, never the key.
  const keyed = KEYED[i.provider];
  useUnsaved({
    names: admin && keyed && key.trim() ? [`${NAMES[i.provider]} API key`] : [],
    saveWords: i.status !== 'not_connected' ? 'Save the new key' : 'Save key',
    busy,
    canSave: keyed ? key.trim().length >= keyed : false,
    error: err,
    onSave: saveKey,
    onDiscard: () => (setKey(''), setErr(null)),
  });
  useUnsaved({
    names: admin && i.provider === 'gmail' && i.status !== 'not_connected' && !sameValue(alias.toLowerCase(), i.intakeAlias) ? ['Intake address'] : [],
    saveWords: 'Save alias',
    busy,
    error: err,
    onSave: saveAlias,
    onDiscard: () => (setAlias(i.intakeAlias ?? ''), setErr(null)),
  });
  const connectedNow = i.status !== 'not_connected';
  const status = STATUS[i.status];
  return (
    <li className="grid gap-x-6 gap-y-1 py-5 sm:grid-cols-[8rem_1fr]">
      <span className="font-display text-[1.125rem]">{NAMES[i.provider]}</span>
      <div>
        <p className="flex flex-wrap items-baseline gap-x-3">
          <span className={`label ${status.tone}`}>{status.word}</span>
          {i.accountLabel ? <span className="text-ink-2">{i.accountLabel}</span> : null}
        </p>
        <p className="meta mt-1">{i.note}</p>
        {i.lastError && !TRIED.has(i.provider) ? <p className="mt-1 text-sm text-alert">{i.lastError}</p> : null}
        <CheckLine i={i} admin={admin} />
        {admin ? (
          <div className="mt-3">
            {keyed ? (
              connectedNow && i.status !== 'error' && !replacing ? (
                <div className="flex flex-wrap items-center gap-5">
                  <Button tone="quiet" onClick={() => setReplacing(true)}>
                    Replace the key
                  </Button>
                  <Button tone="quiet" busy={busy} onClick={() => void disconnect()}>
                    Remove key
                  </Button>
                  {i.provider === 'resend' ? <TestButton provider="resend" run={run} setNote={setNote} /> : null}
                </div>
              ) : (
                <div className="flex flex-wrap items-end gap-4">
                  <div className="min-w-64 flex-1">
                    <TextInput
                      aria-label={`${NAMES[i.provider]} API key`}
                      type="password"
                      autoComplete="off"
                      spellCheck={false}
                      placeholder={KEY_FORMAT[i.provider] ?? (connectedNow ? 'The new API key' : 'API key')}
                      value={key}
                      onChange={(e) => setKey(e.target.value)}
                    />
                  </div>
                  <Button busy={busy} disabled={key.trim().length < keyed} onClick={saveKey}>
                    {connectedNow ? 'Save the new key' : 'Save key'}
                  </Button>
                  {replacing ? (
                    <Button tone="quiet" onClick={() => (setReplacing(false), setKey(''))}>
                      Keep the old one
                    </Button>
                  ) : null}
                </div>
              )
            ) : connectedNow ? (
              <div className="flex flex-wrap items-center gap-5">
                <Button tone="quiet" busy={busy} onClick={() => void disconnect()}>
                  Disconnect
                </Button>
                {i.provider === 'gmail' && !resendOn ? <TestButton provider="gmail" run={run} setNote={setNote} /> : null}
              </div>
            ) : (
              <Button tone="secondary" busy={busy} onClick={() => void connect()} disabled={!i.connectable && i.provider === 'gmail'}>
                Connect {NAMES[i.provider]}
              </Button>
            )}
            {i.provider === 'gmail' && connectedNow ? (
              <div className="mt-4 flex flex-wrap items-end gap-4">
                <div className="min-w-64 flex-1">
                  <Field label="Intake address" hint="An alias on this Google account. Email to it, forwarded to it or copied to it is read: a clear new inquiry becomes a lead, and the rest waits on the Intake page.">
                    {(id) => <TextInput id={id} type="email" value={alias} onChange={(e) => setAlias(e.target.value)} placeholder="name@…" />}
                  </Field>
                </div>
                <Button tone="quiet" onClick={saveAlias}>
                  Save alias
                </Button>
              </div>
            ) : null}
            {i.provider === 'gmail' && connectedNow ? <SendAs resendOn={resendOn} /> : null}
            {i.provider === 'gmail' ? (
              <p className="meta mt-3 max-w-measure">
                Each project’s email is read from this account and never changed: connect the one where all your mail arrives. A project’s Email page suggests the threads with its people.
              </p>
            ) : null}
            {!i.connectable && i.provider === 'gmail' ? (
              <p className="meta mt-2">This deployment has no Google OAuth app yet — a one-time setup step in DEPLOYMENT.md.</p>
            ) : null}
          </div>
        ) : null}
        {note ? <p className="mt-2 text-sm text-ink-2">{note}</p> : null}
        <ErrorLine error={err} />
      </div>
    </li>
  );
}

/**
 * Who the portal's own mail comes from: sign-in links, invites, notices. One
 * of the addresses Gmail lets this account send as, Gmail's default unless
 * one is chosen here; and whether it goes out through Resend, that address's
 * own mail server (signed as its domain) or Gmail's.
 */
function SendAs({ resendOn }: { resendOn: boolean }) {
  const { data, error } = useApi<GmailSendAsDto>(['integrations', 'gmail', 'send-as'], '/integrations/gmail/send-as');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (error) return <p className="meta mt-4">Gmail did not report which addresses it sends as: {errorText(error)}</p>;
  if (!data) return null;
  const current = data.choices.find((c) => c.email === data.current) ?? null;
  const choose = async (sendAs: string | null) => {
    setBusy(true);
    setErr(null);
    try {
      await api('/integrations/gmail', { method: 'PATCH', body: { sendAs } });
      // Every row: Resend's names the address it sends as.
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4">
      <label className="block max-w-xl">
        <span className="label block">The portal’s mail comes from</span>
        <select className="field" value={data.chosen ?? ''} disabled={busy} onChange={(e) => void choose(e.target.value || null)}>
          <option value="">Gmail’s default{data.choices.find((c) => c.isDefault) ? `: ${data.choices.find((c) => c.isDefault)!.email}` : ''}</option>
          {data.choices.map((c) => (
            <option key={c.email} value={c.email}>
              {c.name ? `${c.name} <${c.email}>` : c.email}
            </option>
          ))}
        </select>
      </label>
      {current ? (
        <p className="meta mt-1 max-w-measure">
          {resendOn
            ? `Sign-in links, invites and notices go out as ${current.email}, through Resend.`
            : current.isPrimary || current.through
            ? `Sign-in links, invites and notices go out as ${current.email}${current.through ? `, through ${current.through}` : ''}.`
            : `Sign-in links, invites and notices go out as ${current.email}, through Gmail’s own servers. For mail signed as its own domain, set that address to send through its mail server in Gmail (Settings → Accounts → Send mail as → edit).`}
        </p>
      ) : null}
      <ErrorLine error={err} />
    </div>
  );
}
